/**
 * TopBar — v3 dashboard chrome (80px tall).
 *
 * Left  : breadcrumb trail (from redux). The page title itself lives
 *         inside each page (PageTitle widget for non-table routes,
 *         TableHeader for table routes) — the chrome only shows the
 *         trail so there is exactly one title per screen.
 * Right : account switcher · divider · notifications · language
 *         · theme · user — all four dropdowns share the same
 *         surface (white card, 12px radius, sh-3 shadow, 6px
 *         padding) and item styling so the topbar reads as one
 *         unified control strip.
 *
 * Layout follows the design-system prototype under
 * `src/QHS Lab Design System/ui_kits/dashboard/TopBar.jsx`. The
 * four popovers used to be legacy components (AccountSelector +
 * AlertSection + LanguageSelector + UserMenu) — each with its
 * own visual dialect. They are now rewritten locally against the
 * shared `DropdownMenu` primitive below.
 *
 * widgets/app-shell is the boundary-exempt bridge (see .eslintrc
 * GROUP 1) so we can still read the same Redux state and use
 * the same actions (changeAccount, changeLanguage, logout,
 * setAlertsPreFilter, navigateToRoute, toggleTheme) that the
 * legacy widgets used.
 */
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { UserContext } from '@app/providers';
import { Tooltip, useV2Theme, LanguageSplash } from '@shared/ui';
import { ALL_ROUTES } from '@app/router';
import permissions from '@shared/config/permissions';
import { checkPermission } from '@shared/lib/checkPermissions';
import {
  getSelectedLanguage,
  setSelectedLanguage,
  supportedLanguages,
} from '@shared/config/translations';
import {
  navigateToRoute,
  logout,
  saveAppearance,
  saveUserLanguage,
  THEME_PREFERENCES,
} from 'redux_store';
import { toBackendLocale } from '@entities/user';
import showServerError from '@shared/lib/showError';
import { Breadcrumbs } from '@widgets/breadcrumbs';
import { FeedbackButton } from '@features/send-feedback';

/* =============================================================
   Inline icons — stroke-based, currentColor, 1.5–2 stroke width
   ============================================================= */

const icon = (paths, { size = 16, stroke = 2 } = {}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    {paths}
  </svg>
);

const ChevronDown = ({ size = 14 }) => icon(<polyline points="6 9 12 15 18 9" />, { size });

const ChevronsUpDown = ({ size = 14 }) =>
  icon(
    <>
      <polyline points="7 15 12 20 17 15" />
      <polyline points="7 9 12 4 17 9" />
    </>,
    { size }
  );

const Check = ({ size = 14 }) => icon(<polyline points="20 6 9 17 4 12" />, { size, stroke: 2.5 });

const Pencil = ({ size = 14 }) =>
  icon(
    <>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />
    </>,
    { size }
  );

const Bell = ({ size = 18 }) =>
  icon(
    <>
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </>,
    { size }
  );

const UserIcon = ({ size = 15 }) =>
  icon(
    <>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </>,
    { size }
  );

const KeyIcon = ({ size = 15 }) =>
  icon(
    <>
      <circle cx="8" cy="15" r="4" />
      <path d="m10.85 12.15 7.35-7.35" />
      <path d="m18 8 3-3" />
      <path d="m15 5 3-3" />
    </>,
    { size }
  );

const LogOut = ({ size = 15 }) =>
  icon(
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </>,
    { size }
  );

const Sun = ({ size = 14 }) =>
  icon(
    <>
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2" x2="12" y2="4" />
      <line x1="12" y1="20" x2="12" y2="22" />
      <line x1="4.93" y1="4.93" x2="6.34" y2="6.34" />
      <line x1="17.66" y1="17.66" x2="19.07" y2="19.07" />
      <line x1="2" y1="12" x2="4" y2="12" />
      <line x1="20" y1="12" x2="22" y2="12" />
      <line x1="4.93" y1="19.07" x2="6.34" y2="17.66" />
      <line x1="17.66" y1="6.34" x2="19.07" y2="4.93" />
    </>,
    { size }
  );

const Moon = ({ size = 14 }) =>
  icon(<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />, { size });

// "System" appearance: follow the OS setting.
const Monitor = ({ size = 14 }) =>
  icon(
    <>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </>,
    { size }
  );

/* =============================================================
   Shared dropdown primitives
   ============================================================= */

/** Closes `onClose` when user clicks anywhere outside `ref`. */
const useOutsideClose = (ref, open, onClose) => {
  useEffect(() => {
    if (!open) return undefined;
    const handle = e => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [ref, open, onClose]);
};

/** 38×38 ghost icon button used for bell + theme triggers. */
const IconPill = styled('button')({
  position: 'relative',
  width: 38,
  height: 38,
  borderRadius: 8,
  background: 'transparent',
  border: '1px solid transparent',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--qhs-text-2)',
  cursor: 'pointer',
  transition: 'all 150ms',
  padding: 0,
  '&:hover': {
    background: 'var(--qhs-surface-hover)',
    color: 'var(--qhs-brand)',
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'default',
    color: 'var(--qhs-text-3)',
  },
  '&:disabled:hover': {
    background: 'transparent',
    color: 'var(--qhs-text-3)',
  },
});

/** The popover panel — same chrome for all four dropdowns. */
const DropdownMenu = styled('div', {
  shouldForwardProp: p => p !== '$align' && p !== '$minWidth',
})(({ $align = 'right', $minWidth = 240 }) => ({
  position: 'absolute',
  top: 'calc(100% + 6px)',
  left: $align === 'left' ? 0 : 'auto',
  right: $align === 'right' ? 0 : 'auto',
  minWidth: $minWidth,
  maxHeight: 420,
  overflowY: 'auto',
  background: 'var(--qhs-surface)',
  border: '1px solid var(--qhs-border)',
  borderRadius: 12,
  boxShadow: 'var(--qhs-sh-3)',
  padding: 6,
  zIndex: 100,
}));

const DropdownLabel = styled('div')({
  padding: '6px 10px',
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '1px',
  color: 'var(--qhs-text-3)',
});

const DropdownItem = styled('button', {
  shouldForwardProp: p => p !== '$active' && p !== '$danger',
})(({ $active, $danger }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  padding: '8px 10px',
  background: $active ? 'var(--qhs-surface-hover)' : 'transparent',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'inherit',
  color: $danger ? 'var(--qhs-st-critical-tx)' : 'var(--qhs-text-1)',
  fontSize: 13,
  '&:hover': {
    background: $danger ? 'var(--qhs-st-critical-bg)' : 'var(--qhs-surface-hover)',
  },
}));

const DropdownDivider = styled('div')({
  height: 1,
  background: 'var(--qhs-border)',
  margin: '6px 4px',
});

const DropdownHeader = styled('div')({
  padding: '10px 12px',
  borderBottom: '1px solid var(--qhs-border)',
  marginBottom: 4,
});

const DropdownSection = styled('div')({
  padding: '4px 10px 8px',
});

const DropdownSectionLabel = styled('div')({
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '1px',
  color: 'var(--qhs-text-3)',
  marginBottom: 6,
});

/** Segmented control used inside user dropdown for theme / language. */
const SegmentedWrap = styled('div')({
  display: 'flex',
  gap: 2,
  padding: 2,
  background: 'var(--qhs-surface-hover)',
  borderRadius: 8,
});

const SegmentedOption = styled('button', {
  shouldForwardProp: p => p !== '$active',
})(({ $active }) => ({
  flex: 1,
  padding: '6px 10px',
  fontSize: 12,
  fontWeight: 600,
  fontFamily: 'inherit',
  background: $active ? 'var(--qhs-surface)' : 'transparent',
  color: $active ? 'var(--qhs-text-1)' : 'var(--qhs-text-2)',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  boxShadow: $active ? 'var(--qhs-sh-1)' : 'none',
  transition: 'all 150ms',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  '&:hover:not(:disabled)': {
    color: 'var(--qhs-text-1)',
  },
}));

const Segmented = ({ options, value, onChange }) => (
  <SegmentedWrap role="tablist">
    {options.map(opt => (
      <SegmentedOption
        key={opt.value}
        type="button"
        role="tab"
        aria-selected={opt.value === value}
        $active={opt.value === value}
        onClick={() => {
          if (opt.value !== value) onChange(opt.value);
        }}
      >
        {opt.icon}
        <span>{opt.label}</span>
      </SegmentedOption>
    ))}
  </SegmentedWrap>
);

/** Gradient avatar used by AccountSwitcher + UserDropdown. */
const GradientAvatar = styled('span', {
  shouldForwardProp: p => p !== '$size' && p !== '$round',
})(({ $size = 28, $round = 6 }) => ({
  width: $size,
  height: $size,
  borderRadius: $round === 'full' ? '50%' : $round,
  background: 'linear-gradient(135deg, #00A8FF, #2E48D3)',
  color: '#fff',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: $size >= 30 ? 12 : 11,
  fontFamily: "'Lato', sans-serif",
  flexShrink: 0,
  overflow: 'hidden',
}));

/** Derive 2-character initials from a display name. */
const initialsOf = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

/* =============================================================
   Account switcher
   ============================================================= */

const AccountTrigger = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  padding: '8px 12px 8px 10px',
  background: 'var(--qhs-surface)',
  border: '1.5px solid var(--qhs-border)',
  borderRadius: 8,
  fontFamily: 'inherit',
  fontSize: 13,
  color: 'var(--qhs-text-1)',
  cursor: 'pointer',
  transition: 'border-color 150ms, background-color 150ms',
  '&:hover': {
    borderColor: 'var(--qhs-border-strong)',
    background: 'var(--qhs-surface-hover)',
  },
  '&:disabled': { cursor: 'wait', opacity: 0.6 },
});

const Eyebrow = styled('span')({
  fontSize: 10,
  color: 'var(--qhs-text-3)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
});

const AccountName = styled('span')({
  fontWeight: 600,
  color: 'var(--qhs-text-1)',
  maxWidth: 180,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const AccountSwitcher = () => {
  const dispatch = useDispatch();
  const { changeAccount, changeAccountLoading } = useContext(UserContext);
  const metadata = useSelector(s => s.user?.metadata);
  const titles = useSelector(s => s.i18n?.titles) || {};
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useOutsideClose(ref, open, () => setOpen(false));

  const accounts = metadata?.accounts || [];
  const currentUuid = metadata?.currentAccount?.uuid;
  const currentName = metadata?.currentAccount?.businessName || '';
  const currentAccountPermissions = metadata?.currentAccountPermissions;

  const canEditAccount = useMemo(
    () => checkPermission(currentAccountPermissions, permissions.ACCOUNT_PATCH),
    [currentAccountPermissions]
  );

  const handleEdit = () => {
    setOpen(false);
    dispatch(
      navigateToRoute({
        route: {
          path: ALL_ROUTES.account.edit.path,
          params: { currentAccountUuid: currentUuid },
          title: titles.editAccount,
          clearBreadcrumbs: true,
        },
      })
    );
  };

  if (!currentUuid) return null;

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <AccountTrigger
        type="button"
        onClick={() => setOpen(o => !o)}
        disabled={changeAccountLoading}
      >
        <GradientAvatar>{initialsOf(currentName)}</GradientAvatar>
        <span
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            lineHeight: 1.2,
            minWidth: 0,
          }}
        >
          <Eyebrow>{titles.account || 'Account'}</Eyebrow>
          <AccountName>{currentName}</AccountName>
        </span>
        <span style={{ color: 'var(--qhs-text-3)', marginLeft: 4, display: 'inline-flex' }}>
          <ChevronsUpDown />
        </span>
      </AccountTrigger>

      {open && (
        <DropdownMenu $align="left" $minWidth={280}>
          {canEditAccount && (
            <>
              <DropdownItem type="button" onClick={handleEdit}>
                <span style={iconTileStyle}>
                  <Pencil />
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>
                    {titles.editAccountDetails || 'Edit account details'}
                  </div>
                  <div style={secondaryLineStyle}>{currentName}</div>
                </span>
              </DropdownItem>
              {accounts.length > 0 && <DropdownDivider />}
            </>
          )}

          {accounts.length > 0 && (
            <>
              <DropdownLabel>{titles.switchAccount || 'Switch account'}</DropdownLabel>
              {accounts.map(a => {
                const isCurrent = a.uuid === currentUuid;
                return (
                  <DropdownItem
                    key={a.uuid}
                    type="button"
                    $active={isCurrent}
                    onClick={() => {
                      if (!isCurrent) changeAccount(a.uuid);
                      setOpen(false);
                    }}
                  >
                    <GradientAvatar $size={26}>{initialsOf(a.businessName)}</GradientAvatar>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {a.businessName}
                      </div>
                      {a.accountType && <div style={secondaryLineStyle}>{a.accountType}</div>}
                    </span>
                    {isCurrent && (
                      <span style={{ color: 'var(--qhs-brand)', display: 'inline-flex' }}>
                        <Check />
                      </span>
                    )}
                  </DropdownItem>
                );
              })}
            </>
          )}
        </DropdownMenu>
      )}
    </div>
  );
};

const iconTileStyle = {
  width: 26,
  height: 26,
  borderRadius: 6,
  background: 'var(--qhs-surface-hover)',
  color: 'var(--qhs-text-2)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const secondaryLineStyle = {
  fontSize: 11,
  color: 'var(--qhs-text-3)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

/* =============================================================
   Notifications bell — placeholder for the future in-dashboard
   news widget (release updates). Alerts live in the sidebar now
   (Monitoring → Alerts opens the dropdown with per-group
   breakdown), so until the widget ships the bell renders
   disabled with a coming-soon tooltip.
   ============================================================= */

// Hidden for the restyle release: the in-dashboard news API has not
// shipped yet, and a permanently disabled pill reads as broken. Flip
// to true when the notifications API lands.
const SHOW_NOTIFICATIONS_BELL = false;

const NotificationsBell = () => {
  const tooltips = useSelector(s => s.i18n?.tooltips) || {};
  return (
    <Tooltip title={tooltips.notificationsComingSoon || ''} placement="bottom">
      {/* span wrapper: tooltips don't fire on disabled elements */}
      <span>
        <IconPill type="button" disabled aria-disabled="true">
          <Bell />
        </IconPill>
      </span>
    </Tooltip>
  );
};

// Only languages the dashboard ships a catalogue for.
const LANGUAGE_LABELS = { en: 'EN', es: 'ES' };

// The interceptor already toasts "no permission" for these two; a
// second toast on top of it would be the duplicate-snackbar bug again.
const isPermissionStatus = status => status === 401 || status === 403;

/* =============================================================
   User menu — also hosts Appearance + Language switches
   ============================================================= */

const UserTrigger = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '4px 8px 4px 4px',
  background: 'transparent',
  border: '1px solid transparent',
  borderRadius: 999,
  cursor: 'pointer',
  transition: 'background-color 150ms',
  '&:hover': { background: 'var(--qhs-surface-hover)' },
});

const UserDropdown = () => {
  const dispatch = useDispatch();
  const { preference: themePreference } = useV2Theme();
  const user = useSelector(s => s.user?.user) || {};
  const titles = useSelector(s => s.i18n?.titles) || {};
  const nav = useSelector(s => s.i18n?.nav) || {};
  const notifications = useSelector(s => s.i18n?.notifications) || {};
  const [open, setOpen] = useState(false);
  // True from the language click until the reload takes over: the
  // splash covers the round trip so the page does not look frozen.
  const [switchingLanguage, setSwitchingLanguage] = useState(false);
  const ref = useRef(null);
  useOutsideClose(ref, open, () => setOpen(false));

  const { firstName = '', lastName = '', email } = user;
  const fullName = [firstName, lastName].filter(Boolean).join(' ') || email || 'Account';
  const initials = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || 'U';

  const currentLang = getSelectedLanguage();

  const goto = (path, title) => {
    setOpen(false);
    dispatch(
      navigateToRoute({
        route: { path, title, clearBreadcrumbs: true, removeQueryString: true },
      })
    );
  };

  const handleLogout = () => {
    setOpen(false);
    dispatch(navigateToRoute({ route: { path: ALL_ROUTES.login.path } })).then(() => {
      dispatch(logout());
    });
  };

  // Theme: one PATCH to the preferences endpoint per change. The slice
  // applies the value optimistically on `pending` and rolls back to
  // `previous` on `rejected`; the toast is the only thing left to do here.
  const handleThemeChange = next => {
    if (next === themePreference) return;
    dispatch(saveAppearance({ theme: next, previous: themePreference })).then(result => {
      if (result.error && !isPermissionStatus(result.payload?.status)) {
        showServerError(dispatch, notifications.somethingWentWrong);
      }
    });
  };

  // Language: one PATCH to the user endpoint per change, and only AFTER
  // it succeeds does the language switch. Switching first would mean a
  // reload (i18next resources load on init only, as in the legacy
  // LanguageSelector), and there is nothing left to roll back after a
  // reload — so the order is save, then apply, then reload.
  const handleLanguageChange = lang => {
    if (lang === currentLang) return;
    const preferredLanguage = toBackendLocale(lang);
    if (!preferredLanguage) return;
    setOpen(false);
    setSwitchingLanguage(true);
    dispatch(saveUserLanguage({ preferredLanguage })).then(result => {
      if (result.error) {
        setSwitchingLanguage(false);
        if (!isPermissionStatus(result.payload?.status)) {
          showServerError(dispatch, notifications.somethingWentWrong);
        }
        return;
      }
      // Persist locally and reload; deliberately NOT dispatching
      // changeLanguage first — that re-renders every string in the new
      // language under the splash a beat before the reload blanks the
      // page (the same stutter the public switcher avoids). On reload
      // translations.js reads the stored language and the i18n slice
      // initialises from it.
      setSelectedLanguage(lang);
      window.location.reload();
    });
  };

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      {switchingLanguage && <LanguageSplash overlay />}
      <UserTrigger type="button" onClick={() => setOpen(o => !o)}>
        <GradientAvatar $size={32} $round="full">
          {initials}
        </GradientAvatar>
        <span style={{ color: 'var(--qhs-text-3)', display: 'inline-flex' }}>
          <ChevronDown size={12} />
        </span>
      </UserTrigger>

      {open && (
        <DropdownMenu $minWidth={260}>
          <DropdownHeader>
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--qhs-text-1)' }}>
              {fullName}
            </div>
            {email && (
              <div style={{ fontSize: 11, color: 'var(--qhs-text-3)', marginTop: 2 }}>{email}</div>
            )}
          </DropdownHeader>

          <DropdownItem
            type="button"
            onClick={() => goto(ALL_ROUTES.profile.edit.path, titles.editProfile)}
          >
            <span style={{ color: 'var(--qhs-text-3)', display: 'inline-flex' }}>
              <UserIcon />
            </span>
            <span>{titles.editProfile || 'Edit profile'}</span>
          </DropdownItem>
          <DropdownItem
            type="button"
            onClick={() => goto(ALL_ROUTES.profile.changePassword.path, titles.changePassword)}
          >
            <span style={{ color: 'var(--qhs-text-3)', display: 'inline-flex' }}>
              <KeyIcon />
            </span>
            <span>{titles.changePassword || 'Change password'}</span>
          </DropdownItem>

          <DropdownDivider />

          <DropdownSection>
            <DropdownSectionLabel>{titles.appearance || 'Appearance'}</DropdownSectionLabel>
            <Segmented
              value={themePreference}
              onChange={handleThemeChange}
              options={[
                { value: THEME_PREFERENCES.light, label: titles.light || 'Light', icon: <Sun /> },
                { value: THEME_PREFERENCES.dark, label: titles.dark || 'Dark', icon: <Moon /> },
                {
                  value: THEME_PREFERENCES.auto,
                  label: titles.system || 'System',
                  icon: <Monitor />,
                },
              ]}
            />
          </DropdownSection>

          {supportedLanguages.length > 1 && (
            <DropdownSection>
              <DropdownSectionLabel>{titles.language || 'Language'}</DropdownSectionLabel>
              <Segmented
                value={currentLang}
                onChange={handleLanguageChange}
                options={supportedLanguages.map(l => ({
                  value: l,
                  label: LANGUAGE_LABELS[l] || l.toUpperCase(),
                }))}
              />
            </DropdownSection>
          )}

          <DropdownDivider />

          <DropdownItem type="button" $danger onClick={handleLogout}>
            <span style={{ display: 'inline-flex' }}>
              <LogOut />
            </span>
            <span>{nav.logOut || 'Sign out'}</span>
          </DropdownItem>
        </DropdownMenu>
      )}
    </div>
  );
};

/* =============================================================
   Topbar layout
   ============================================================= */

const Header = styled('header')({
  gridArea: 'topbar',
  height: 80,
  // A grid item keeps `min-width: auto` and would overflow a track
  // narrower than its content; 0 lets the bar shrink with the column
  // and the breadcrumbs (the one flexible item) truncate.
  minWidth: 0,
  background: 'var(--qhs-surface)',
  borderBottom: '1px solid var(--qhs-border)',
  display: 'flex',
  alignItems: 'center',
  padding: '0 24px',
  gap: 20,
  transition: 'background-color 220ms ease, border-color 220ms ease',
});

const LeftSection = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  minWidth: 0,
  flex: '0 1 auto',
});

const Spacer = styled('div')({ flex: 1 });

const Actions = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  flexShrink: 0,
});

const Divider = styled('div')({
  width: 1,
  height: 32,
  background: 'var(--qhs-border)',
  margin: '0 4px',
});

const TopBar = () => (
  <Header>
    <LeftSection>
      <Breadcrumbs />
    </LeftSection>

    <Spacer />

    <AccountSwitcher />
    <Divider />
    <Actions>
      <FeedbackButton />
      {SHOW_NOTIFICATIONS_BELL && <NotificationsBell />}
      <UserDropdown />
    </Actions>
  </Header>
);

export default TopBar;
